import {
  SHAPE_PART_ID,
  SQFT_APPLIANCES,
} from "../../../../../../constants";
import { CountertopPositions } from "../../../../../../helpers/sqftCabinetsDataMapper";
import { getShapeStartBounds } from "./cabinetPlacementResolver";

const COUNTERTOP_SIGNATURE_PRECISION = 1000;
const COUNTERTOP_AFFECTING_PART_IDS = [
  SHAPE_PART_ID.lower_cabinet_boxes,
  SHAPE_PART_ID.vanitie_cabinet_boxes,
  SHAPE_PART_ID.vanity_cabinet_boxes,
  SHAPE_PART_ID.bottom_cabinet,
  SHAPE_PART_ID.tall_cabinet_boxes,
  SHAPE_PART_ID.tall_cabinet,
];
const COUNTERTOP_AFFECTING_SUB_TYPES = [
  SHAPE_PART_ID.dishwasher,
  SQFT_APPLIANCES.dishwasher,
];
const MOVABLE_COUNTERTOP_OVERLAP_RATIO = 0.7;

function roundCountertopSignatureValue(value) {
  return Math.round((Number(value) || 0) * COUNTERTOP_SIGNATURE_PRECISION) / COUNTERTOP_SIGNATURE_PRECISION;
}

function canShapeAffectCountertops(shape) {
  if (!shape) {
    return false;
  }

  return COUNTERTOP_AFFECTING_PART_IDS.includes(shape.partId)
    || COUNTERTOP_AFFECTING_PART_IDS.includes(shape.parentPartId)
    || COUNTERTOP_AFFECTING_SUB_TYPES.includes(shape.subType);
}

// Describes the generated countertop shape while ignoring absolute X/Z
// placement, so a pure move can reuse the existing GLB instance.
function getCountertopGeometrySignature(position) {
  const contourPoints = position.contour_points || [];
  const origin = contourPoints[0] || { x: 0, y: 0 };
  const normalizedContour = contourPoints.map(point => ({
    x: roundCountertopSignatureValue(point.x - origin.x),
    y: roundCountertopSignatureValue(point.y - origin.y),
  }));

  return JSON.stringify({
    contour: normalizedContour,
    edgeUrl: position.edge_url,
    height: roundCountertopSignatureValue(position.height),
    overhang: position.overhang || [],
    partId: position.partId,
    textureRotation: roundCountertopSignatureValue(position.texture_rotation),
    textureUrl: position.texture_url,
    width: roundCountertopSignatureValue(position.width),
    y: roundCountertopSignatureValue(position.y),
  });
}

function getCountertopOrigin(position) {
  return position && position.contour_points && position.contour_points[0];
}

function getDistanceBetweenCountertopOrigins(first, second) {
  const firstOrigin = getCountertopOrigin(first);
  const secondOrigin = getCountertopOrigin(second);

  if (!firstOrigin || !secondOrigin) {
    return Infinity;
  }

  const xDiff = firstOrigin.x - secondOrigin.x;
  const yDiff = firstOrigin.y - secondOrigin.y;

  return (xDiff * xDiff) + (yDiff * yDiff);
}

function getCountertopBounds(position) {
  const contourPoints = position.contour_points || [];
  if (!contourPoints.length) {
    return null;
  }

  return contourPoints.reduce((bounds, point) => ({
    left: Math.min(bounds.left, point.x),
    right: Math.max(bounds.right, point.x),
    top: Math.min(bounds.top, point.y),
    bottom: Math.max(bounds.bottom, point.y),
  }), {
    left: contourPoints[0].x,
    right: contourPoints[0].x,
    top: contourPoints[0].y,
    bottom: contourPoints[0].y,
  });
}

function getBoundsOverlapArea(firstBounds, secondBounds) {
  if (!firstBounds || !secondBounds) {
    return 0;
  }

  const xOverlap = Math.max(0, Math.min(firstBounds.right, secondBounds.right) - Math.max(firstBounds.left, secondBounds.left));
  const yOverlap = Math.max(0, Math.min(firstBounds.bottom, secondBounds.bottom) - Math.max(firstBounds.top, secondBounds.top));

  return xOverlap * yOverlap;
}

function getBoundsArea(bounds) {
  if (!bounds) {
    return 0;
  }

  return Math.max(0, bounds.right - bounds.left) * Math.max(0, bounds.bottom - bounds.top);
}

function canMoveCountertopWithShape(countertopBounds, shapeBounds) {
  const countertopArea = getBoundsArea(countertopBounds);
  if (!countertopArea) {
    return false;
  }

  const overlapArea = getBoundsOverlapArea(countertopBounds, shapeBounds);
  return overlapArea / countertopArea >= MOVABLE_COUNTERTOP_OVERLAP_RATIO;
}

// Matches old and new countertop payloads that have the same generated geometry
// and returns instance offsets needed to retarget them without a Composer call.
function buildCountertopReuseTransitions(previousPositions, nextPositions, modelScale) {
  if (!previousPositions.length && !nextPositions.length) {
    return [];
  }

  if (previousPositions.length !== nextPositions.length) {
    return null;
  }

  const previousBySignature = previousPositions.reduce((result, position) => {
    const signature = getCountertopGeometrySignature(position);
    if (!result[signature]) {
      result[signature] = [];
    }
    result[signature].push(position);
    return result;
  }, {});
  const transitions = [];

  for (const nextPosition of nextPositions) {
    const signature = getCountertopGeometrySignature(nextPosition);
    const matchingPreviousPositions = previousBySignature[signature];

    if (!matchingPreviousPositions || matchingPreviousPositions.length === 0) {
      return null;
    }

    let previousPositionIndex = 0;
    let previousPositionDistance = Infinity;

    matchingPreviousPositions.forEach((previousPositionCandidate, index) => {
      const distance = getDistanceBetweenCountertopOrigins(previousPositionCandidate, nextPosition);
      if (distance < previousPositionDistance) {
        previousPositionDistance = distance;
        previousPositionIndex = index;
      }
    });

    const previousPosition = matchingPreviousPositions.splice(previousPositionIndex, 1)[0];
    const previousOrigin = getCountertopOrigin(previousPosition);
    const nextOrigin = getCountertopOrigin(nextPosition);

    if (!previousOrigin || !nextOrigin) {
      return null;
    }

    transitions.push({
      from: previousPosition,
      to: nextPosition,
      positionOffset: {
        x: (nextOrigin.x - previousOrigin.x) * modelScale,
        z: (nextOrigin.y - previousOrigin.y) * modelScale,
      },
    });
  }

  return transitions;
}

export default class CountertopDragPreviewController {
  constructor(threeScene) {
    this.threeScene = threeScene;
  }

  canSyncForShape(shape) {
    return canShapeAffectCountertops(shape);
  }

  // Captures the Composer input before a drag commit so the post-commit payload
  // can be compared for cheap instance reuse.
  getCurrentPositions(shape) {
    return this.canSyncForShape(shape)
      ? CountertopPositions.prepareCountertopPositions()
      : [];
  }

  // Finds currently loaded countertop instances that visually belong to the
  // dragged cabinet and should follow it during preview movement.
  captureTargets(shape) {
    const modelsLoader = this.threeScene && this.threeScene.modelsLoader;
    if (!modelsLoader || !this.canSyncForShape(shape)) {
      return [];
    }

    const shapeBounds = getShapeStartBounds(shape);
    if (!shapeBounds) {
      return [];
    }

    return CountertopPositions.prepareCountertopPositions()
      .map((position) => {
        const countertopBounds = getCountertopBounds(position);
        const instance = modelsLoader.getModelInstance('countertops', position);
        const overlapArea = getBoundsOverlapArea(countertopBounds, shapeBounds);

        if (!instance || overlapArea <= 0) {
          return null;
        }

        return {
          instance,
          mode: canMoveCountertopWithShape(countertopBounds, shapeBounds) ? 'movable' : 'hidden',
          startPosition: instance.position.clone(),
          startVisible: instance.visible,
        };
      })
      .filter(Boolean);
  }

  // Applies preview-only movement. The 2D workspace remains unchanged until the
  // drag commit, so these offsets must be reset before final sync/reload.
  moveTargets(targets, previewDelta) {
    if (!targets || !targets.length) {
      return;
    }

    targets.forEach((target) => {
      if (target.mode === 'hidden') {
        target.instance.visible = false;
        return;
      }

      target.instance.position.set(
        target.startPosition.x + previewDelta.x,
        target.startPosition.y,
        target.startPosition.z + previewDelta.z
      );
    });
  }

  // Restores previewed countertop instances to their original scene transform.
  resetTargets(targets) {
    if (!targets || !targets.length) {
      return;
    }

    targets.forEach((target) => {
      target.instance.position.copy(target.startPosition);
      target.instance.visible = target.startVisible;
    });
  }

  hasHiddenTargets(targets) {
    return Array.isArray(targets) && targets.some(target => target.mode === 'hidden');
  }

  // Finalizes countertop state after the 2D workspace commit. Returns true when
  // geometry changed and the caller should perform the delayed Composer reload.
  syncAfterCommit(previousPositions, targets) {
    const modelsLoader = this.threeScene && this.threeScene.modelsLoader;
    if (!modelsLoader) {
      return false;
    }

    this.resetTargets(targets);

    const modelScale = this.threeScene.modelScale || 1;
    const nextPositions = CountertopPositions.prepareCountertopPositions();
    const transitions = this.hasHiddenTargets(targets)
      ? null
      : buildCountertopReuseTransitions(previousPositions, nextPositions, modelScale);

    if (!transitions) {
      return true;
    }

    if (transitions.length === 0) {
      return false;
    }

    return !modelsLoader.retargetModelInstances('countertops', transitions);
  }
}
